import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import axios from 'axios';
import { toast } from 'react-toastify';
import ScheduleDayData from './ScheduleDay';
import ScheduleNewSearchData from './ScheduleNewSearch';
import * as DateHelpers from './DateHelpers';
import * as _ from 'lodash';
import authService from './api-authorization/AuthorizeService';

interface ScheduleNew {
    monthOptions: GenericList[];
    yearOptions: GenericList[];
    scheduleBranchOptions: GenericList[];
    month: number;
    year: number;
    scheduleDate: string;
    branchFilterId: number;
    noteTypes: GenericList[];
    scheduleDay: ScheduleDay[];
    monthAmountFormatted: string;
    monthlyBudget: number;
    monthlyBudgetFormatted: string;
    allowEdit: boolean;
    allowAllocateToPastDate: boolean;
    allowUpdateDayLimits: boolean;
    allowUpdateMonthlyBudget: boolean;
    allowLock: boolean;
    allowGetAccountBalance: boolean;
    errors: any;
    loading: boolean;
    startDate: string;
    endDate: string;
    error: boolean;
    isLoading: boolean;     //loading for scroll
    hasMoreStart: boolean;
    hasMoreEnd: boolean;
    scheduleWarningNo: number;
    scheduleBookedNo: number;
    loadingDay: boolean;
    publicHolidays: PublicHoliday[];
    shutdownPeriods: ShutdownPeriod[];
    reasonOptions: GenericList[];
    scheduleTypes: GenericList[];
    statusOptions: GenericList[];
    showSearch: boolean;
    searchScheduleId: number;
}

interface ScheduleDay {
    scheduledDate: string;
    scheduledDateActual: string;
    scheduledDateFormatted: string;
    scheduledDateFormattedSmall: string;
    lightDutyMins: string;
    voidMins: string;
    kwikstageTotal: string;
    notes: string;
    shortNotes: string;
    walkWayGuardRailPitchMax: number;
    walkWayGuardRailPitchMaxRemaining: number;
    voidMax: number;
    voidMaxRemaining: number;
    kwikstageMax: number;
    kwikstageMaxRemaining: number;
    dayAmountFormatted: string;
    status: string;
    statusId: number;
    isCurrentDay: boolean;
    jobs: ScheduleJobs[];
    scheduleErrors: string[];
}

interface ScheduleJobs {
    jobIndex: number;
    itemIndex: number;
    id: number;
    jobId: number;
    jobQuoteItemId: number;
    orderManual: string;
    siteAddress: string;
    customerId: number;
    customer: string;
    contact: string;
    siteContact: string;
    description: string;
    manualItem: boolean;
    type: string;
    typeId: number;
    childTypeId: number;
    itemId: number;
    item: string;
    itemShortcut: string;
    itemIsKwikstage: boolean;
    itemIsVoid: boolean;
    quoteNumber: string;
    quotedAmountFormatted: string;
    daysHire: number;
    quantityTotal: string;  //quoted
    quantityScheduled: number;
    onOffhireDate: string;
    onOffhireDateFormatted: string;
    purchaseOrderNumber: string;
    accountTypeId: number;
    accountType: string;
    accountBalance: number;
    accountBalanceFormatted: string;
    creditLimit: number;
    remainingBalance: number;
    remainingBalanceFormatted: string;
    jobAmount: number;
    jobAmountFormatted: string;
    accountChecked: boolean;
    goAhead: boolean;
    pickupScheduled: boolean;
    callout: boolean;
    copy: boolean;
    showRedNotes: boolean;
    scheduleNotes: string;
    runsheetNotes: string;
    truckAccess: string;
    parking: string;
    siteClean: string;
    powerlines: string;
    showGreenNotes: boolean;
    itemStatusId: number;
    status: string;
    reckonUpdatesRequired: boolean;
    customerUpdatesRequired: boolean;
    overhireAllowed: boolean;
    unitOfMeasure: string;
    quantityType: string;
    bringForwardDate: string;
    bookedBy: string;
    planChanges: boolean;
    jobConfirmed: boolean;
    emailSent: boolean;
    jobOrder: number;
    allowEdit: boolean;
    allowDelete: boolean;
    allowExtend: boolean;
    allowInvoice: boolean;
    extraHours: number;
    items: ScheduleItems[];
    totalItems: number;
    itemQuantityType: string;
    showItems: boolean;
    isPlaceholder: boolean;
    rowVersionTimestamp: Uint8Array;
    isDirty: boolean;
}

interface ScheduleItems {
    jobIndex: number;
    itemIndex: number;
    id: number;
    jobId: number;
    jobQuoteItemId: number;
    orderManual: string;
    siteAddress: string;
    customerId: number;
    customer: string;
    contact: string;
    siteContact: string;
    description: string;
    manualItem: boolean;
    type: string;
    typeId: number;
    itemId: number;
    item: string;
    itemShortcut: string;
    itemIsKwikstage: boolean;
    quoteNumber: string;
    quotedAmountFormatted: string;
    daysHire: number;
    quantityTotal: string;  //quoted
    quantityScheduled: number;
    onOffhireDate: string;
    onOffhireDateFormatted: string;
    purchaseOrderNumber: string;
    accountTypeId: number;
    accountType: string;
    jobAmount: number;
    jobAmountFormatted: string;
    accountChecked: boolean;
    goAhead: boolean;
    pickupScheduled: boolean;
    callout: boolean;
    copy: boolean;
    showRedNotes: boolean;
    scheduleNotes: string;
    runsheetNotes: string;
    truckAccess: string;
    parking: string;
    siteClean: string;
    powerlines: string;
    showGreenNotes: boolean;
    itemStatusId: number;
    status: string;
    reckonUpdatesRequired: boolean;
    customerUpdatesRequired: boolean;
    overhireAllowed: boolean;
    unitOfMeasure: string;
    quantityType: string;
    bringForwardDate: string;
    bookedBy: string;
    planChanges: boolean;
    jobConfirmed: boolean;
    emailSent: boolean;
    jobOrder: number;
    allowEdit: boolean;
    allowDelete: boolean;
    allowExtend: boolean;
    allowInvoice: boolean;
    extraHours: number;
    rowVersionTimestamp: Uint8Array;
    isDirty: boolean;
}

interface ItemOrder {
    scheduleId: number;
    jobOrder: number;
    rowVersionTimestamp: Uint8Array;
}

interface GenericList {
    id: number;
    name: string;
}

interface PublicHoliday {
    name: string;
    date: string;
}

interface ShutdownPeriod {
    startDate: string;
    endDate: string;
}

interface SaveScheduleJob {
    id: number;
    orderManual: string;
    jobOrder: number;
    onOffhireDate: string;
    purchaseOrderNumber: string;
    accountChecked: boolean;
    goAhead: boolean;
    rowVersionTimestamp: Uint8Array
    itemIndex: number;
}

export class ScheduleNewData extends React.Component<RouteComponentProps<{}>, ScheduleNew> {
    constructor(props: any) {
        super(props);

        var date = new Date();
        var currentMonth = (date.getMonth() + 1);
        var currentYear = date.getFullYear();
        var day = date.getDay();
        var lastDay = DateHelpers.getLastDay(date);
        let isLastDay = lastDay === day; 
        let is2ndLastDay = (lastDay - 1) === day;
        var endDate = DateHelpers.addDaysToCurrentDate(2);

        this.state = {
            monthOptions: [],
            yearOptions: [],
            scheduleBranchOptions: [],
            monthAmountFormatted: "$0.00",
            monthlyBudget: 0,
            monthlyBudgetFormatted: "$0.00",
            month: currentMonth,
            year: currentYear,
            scheduleDate: "",
            branchFilterId: 1,
            noteTypes: [],
            scheduleDay: [],
            allowEdit: true,
            allowAllocateToPastDate: false,
            allowUpdateDayLimits: false,
            allowUpdateMonthlyBudget: false,
            allowLock: false,
            allowGetAccountBalance: false,
            errors: {},
            loading: true,
            startDate: date.toString(),
            endDate: endDate,
            error: false,
            isLoading: false,
            hasMoreStart: day === 1 || day === 2 ? false : true,
            hasMoreEnd: isLastDay || is2ndLastDay ? false : true,
            publicHolidays: [],
            shutdownPeriods: [],
            reasonOptions: [],
            scheduleTypes: [],
            statusOptions: [],
            scheduleWarningNo: 3,
            scheduleBookedNo: 5,
            loadingDay: false,
            showSearch: false,
            searchScheduleId: 0
        }

    }

    componentDidMount() {
        this.getData();
    }

    async getData() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/User/CheckUserPermission?Permission=CreateEditSchedule&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data == true) {
                //get items to be scheduled
                var today = DateHelpers.getCurrentDate();
                axios.get('api/Schedule/GetScheduleAll?scheduleDate=' + today + "&getMultiple=true&scheduleBranchId=" + this.state.branchFilterId + '&SubId=' + user.sub + '&CompanyId=2', {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(res => {
                    let scheduleDays = this.setScheduleData(res.data.schedule);
                    this.setState({
                        scheduleDay: scheduleDays, branchFilterId: res.data.scheduleBranchId, monthAmountFormatted: res.data.monthAmountFormatted, monthlyBudget: res.data.monthlyBudget, monthlyBudgetFormatted: res.data.monthlyBudgetFormatted,
                        monthOptions: res.data.monthOptions, yearOptions: res.data.yearOptions, scheduleBranchOptions: res.data.scheduleBranchOptions, scheduleWarningNo: res.data.scheduleWarningNo, scheduleBookedNo: res.data.scheduleBookedNo,
                        noteTypes: res.data.noteTypes, allowEdit: res.data.allowEdit, allowAllocateToPastDate: res.data.allowAllocateToPastDate, allowUpdateDayLimits: res.data.allowUpdateDayLimits, allowUpdateMonthlyBudget: res.data.allowUpdateMonthlyBudget,
                        allowLock: res.data.allowLock, allowGetAccountBalance: res.data.allowGetAccountBalance, publicHolidays: res.data.publicHolidays, shutdownPeriods: res.data.shutdownPeriods, reasonOptions: res.data.reasonOptions,
                        scheduleTypes: res.data.scheduleTypes, statusOptions: res.data.statusOptions, loading: false
                    });

                    this.executeScroll();
                })
                .catch(error => {
                    toast.error(error.message);
                });
            } else {
                //redirect to 403 permission denied
                this.props.history.push("/accessdenied")
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    executeScroll = () => {
        var divToScrollTo = document.getElementById('currentday');
        var header = document.getElementById('header');
        var headerHeight = 0;
        if (header) {
            headerHeight = header.offsetHeight;
        }

        if (divToScrollTo) {
            var options: ScrollToOptions = {
                behavior: "smooth",
                left: 0,
                top: divToScrollTo.offsetTop - headerHeight - 50
            };
            window.scrollTo(options);
        }
    }

    async getSchedule(date: string, getMultiple: boolean, scrollToDay: boolean) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        this.setState({ isLoading: true });
        axios.get('api/Schedule/GetSchedule?scheduleDate=' + date + "&getMultiple=" + getMultiple + "&scheduleBranchId=" + this.state.branchFilterId + '&SubId=' + user.sub + '&CompanyId=2', {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            let schedule = res.data.schedule;
            let scheduleDays = this.setScheduleData(schedule);
                
            this.setState({ scheduleDay: scheduleDays, monthAmountFormatted: res.data.monthAmountFormatted, monthlyBudget: res.data.monthlyBudget, monthlyBudgetFormatted: res.data.monthlyBudgetFormatted, loading: false, isLoading: false });
            if (scrollToDay) {
                this.executeScroll();
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    setScheduleData = (scheduleDays: ScheduleDay[]) => {
        for (var x = 0; x < scheduleDays.length; x++) {
            let jobs = scheduleDays[x].jobs;
            var jobIndex = 1;
            for (var i = 0; i < jobs.length; i++) {
                let job = jobs[i];
                job.itemIndex = i;
                if (job.totalItems === 0) {
                    job.jobIndex = jobIndex;
                    jobIndex += 1;
                } else {
                    job.jobIndex = jobIndex;  //not used but can't be null else errors
                }
                var items = job.items;
                for (var y = 0; y < items.length; y++) {
                    items[y].jobIndex = jobIndex;
                    items[y].itemIndex = y;
                    jobIndex += 1;
                }
            }
        }
        return scheduleDays;
    }

    refresh = () => {
        var date = DateHelpers.getCurrentDate();
        var getMultiple = true;
        if (this.state.scheduleDate != null && this.state.scheduleDate != "") {
            //get single date
            date = this.state.scheduleDate;
            getMultiple = false;
            this.setState({ startDate: date, endDate: date, hasMoreStart: false, hasMoreEnd: false, scheduleDay: [] });
        } else {
            var checkDate = new Date(date); //todays date
            var startDate = date;
            var hasMoreStart = true;

            if (((checkDate.getMonth() + 1) != this.state.month) || (checkDate.getFullYear() != this.state.year)) {
                //use the first of the month
                var newdate = DateHelpers.get1st(this.state.month, this.state.year);
                startDate = newdate;

                //endDate = DateHelpers.addDaysToDate(newdate, 2);
                hasMoreStart = false;

                //update date to start date
                date = startDate;
            }

            var dayCalc = 2;
            var dayofWeek = DateHelpers.getDayOfWeek(startDate);
            if (dayofWeek === 4 || dayofWeek === 5 || dayofWeek === 6) {
                dayCalc = 4;
            } else if (dayofWeek === 0) {
                dayCalc = 3;
            }
            var endDate = DateHelpers.addDaysToDate(startDate, dayCalc);

            this.setState({ searchScheduleId: 0, startDate: startDate, endDate: endDate, hasMoreStart: hasMoreStart, hasMoreEnd: true, scheduleDay: [] });
        }

        this.getSchedule(date, getMultiple, false);
    }

    //SEARCH
    search = () => {
        this.setState({ showSearch: true, searchScheduleId: 0 });
    }

    hideSearchModal = () => {
        this.setState({ showSearch: false, searchScheduleId: 0 });
    }

    viewSearchDay = (searchDate: string, searchScheduleId: number) => {
        //var endDate = DateHelpers.addDaysToDate(searchDate, 2);

        this.setState({ showSearch: false, scheduleDate: searchDate, searchScheduleId: searchScheduleId, startDate: searchDate, endDate: searchDate, scheduleDay: [] });
        this.getSchedule(searchDate, false, false);
    }

    resetSearch = () => {
        var date = DateHelpers.getCurrentDate();
        var checkDate = new Date(date); //todays date
        var startDate = date;
        var dayCalc = 2;
        var dayofWeek = DateHelpers.getDayOfWeek(startDate);
        if (dayofWeek === 4 || dayofWeek === 5 || dayofWeek === 6) {
            dayCalc = 4;
        } else if (dayofWeek === 0) {
            dayCalc = 3;
        }
        var endDate = DateHelpers.addDaysToDate(date, dayCalc);
        var hasMoreStart = true;
        var currentMonth = (checkDate.getMonth() + 1);
        var currentYear = checkDate.getFullYear();
        
        this.setState({ searchScheduleId: 0, scheduleDate: "", month: currentMonth, year: currentYear, startDate: startDate, endDate: endDate, hasMoreStart: hasMoreStart, hasMoreEnd: true, scheduleDay: [] });

        this.getSchedule(date, true, true);
    }

    updateSearchScheduleDays = (datesToUpate: string[]) => {
        //update all the days where we rescheduled items via the search screen
        for (var i = 0; i < datesToUpate.length; i ++) {
            this.updateDayData(datesToUpate[i], false);
        }
    }

    //END SEARCH

    //ADD NEW / UPDATE
    updateScheduleDay = (scheduleDate: string, scheduleDay: ScheduleDay) => {
        var scheduleDays = this.state.scheduleDay;
        var existDayIndex = scheduleDays.findIndex(sd => sd.scheduledDate === scheduleDate);
        if (existDayIndex >= 0) {
            scheduleDay.scheduleErrors = [];    //schedule day has been updated so clear any errors
            scheduleDays[existDayIndex] = scheduleDay;
        }
        this.setState({ scheduleDay: scheduleDays });
    }

    updateDayData = (scheduleDate: string, refreshDayOnly: boolean) => {
        this.updateScheduleDayData(scheduleDate, refreshDayOnly);
    }

    async updateScheduleDayData(scheduleDate: string, refreshDayOnly: boolean) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        let scheduleDays = this.state.scheduleDay;  //get current days with jobs

        //check if day is loaded before trying to update
        var existingDayIndex = this.state.scheduleDay.findIndex(sd => sd.scheduledDate === scheduleDate);
        if (existingDayIndex < 0) {
            return;
        }
        //can't set state after this as it reloads the page and changes the scroll position
        var existScrollPos = document.documentElement.scrollTop || document.body.scrollTop;

        let getMultiple = true;
        if (refreshDayOnly || (this.state.scheduleDate != null && this.state.scheduleDate != "")) {
            //get single date
            getMultiple = false;
        }

        var jobShowItems: number[] = [];
        var day = this.state.scheduleDay.find(d => d.scheduledDate === scheduleDate);
        if (day) {
            //get any open jobs
            var jobs = day.jobs.filter(j => j.showItems);
            for (var i = 0; i < jobs.length; i++) {
                //add any sub-jobs just in case they delete the parent job
                var items = jobs[i].items;
                for (var x = 0; x < items.length; x++) {
                    jobShowItems.push(jobs[i].items[x].id);
                }
            }
        }

        axios.get('api/Schedule/GetScheduleAll?scheduleDate=' + scheduleDate + "&getMultiple=" + getMultiple + "&scheduleBranchId=" + this.state.branchFilterId + '&SubId=' + user.sub + '&CompanyId=2', {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            let newScheduleDays = this.setScheduleData(res.data.schedule);

            var currDay = scheduleDays.find(s => s.scheduledDate === scheduleDate);
            if (refreshDayOnly && newScheduleDays.length > 0) {
                //we need to just replace the single date
                currDay = newScheduleDays[0];
                var currDayIndex = scheduleDays.findIndex(s => s.scheduledDate === scheduleDate);
                //replace data with updated data for selected day
                scheduleDays[currDayIndex] = currDay;
            } else {
                scheduleDays = newScheduleDays;
                currDay = scheduleDays.find(s => s.scheduledDate === scheduleDate);
            }

            //re-open any items for jobs that were set to open
            if (currDay && jobShowItems.length > 0) {
                var currJobs = currDay.jobs;
                for (var y = 0; y < jobShowItems.length; y++) {
                    var job = currJobs.find(s => s.id == jobShowItems[y]);
                    if (job) {
                        job.showItems = true;
                    }
                }
            }

            this.setState({ scheduleDay: scheduleDays, monthOptions: res.data.monthOptions, yearOptions: res.data.yearOptions, scheduleBranchOptions: res.data.scheduleBranchOptions, noteTypes: res.data.noteTypes, allowEdit: res.data.allowEdit, allowAllocateToPastDate: res.data.allowAllocateToPastDate, loading: false, loadingDay: false })
            window.scrollTo(0, existScrollPos);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    saveDayOrder = (scheduleDate: string, items: ItemOrder[]) => {
        this.saveOrder(scheduleDate, items);
    }

    async saveOrder(scheduleDate: string, items: ItemOrder[]) {
        const token = await authService.getAccessToken();

        axios.post('api/Schedule/SaveScheduleItemOrder', items, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                this.updateDayData(scheduleDate, false);
                toast.success("Schedule Saved");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    saveDayData = (scheduleDate: string) => {
        this.saveDay(scheduleDate);
    }

    async saveDay(scheduleDate: string) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        var scheduleDays = this.state.scheduleDay;
        var existDayIndex = scheduleDays.findIndex(sd => sd.scheduledDate === scheduleDate);
        if (existDayIndex >= 0) {
            var scheduleDay = this.state.scheduleDay[existDayIndex];
            var changedJobs = scheduleDay.jobs.filter(s => s.isDirty);

            let saveJobs: SaveScheduleJob[] = [];
            for (var i = 0; i < changedJobs.length; i++) {
                let changedJob = changedJobs[i];

                if (changedJob.totalItems === 0) {
                    //save job
                    let saveJob: SaveScheduleJob = {
                        id: changedJob.id,
                        itemIndex: changedJob.itemIndex,
                        orderManual: changedJob.orderManual,
                        jobOrder: changedJob.jobOrder,
                        purchaseOrderNumber: changedJob.purchaseOrderNumber,
                        onOffhireDate: changedJob.onOffhireDate,
                        accountChecked: changedJob.accountChecked,
                        goAhead: changedJob.goAhead,
                        rowVersionTimestamp: changedJob.rowVersionTimestamp
                    };
                    saveJobs.push(saveJob);
                } else {
                    //save all items
                    for (var y = 0; y < changedJob.items.length; y++) {
                        let changedItem = changedJob.items[y];

                        let saveJob: SaveScheduleJob = {
                            id: changedItem.id,
                            itemIndex: changedItem.itemIndex,
                            orderManual: changedJob.orderManual,    //this one at job level so save for all items
                            jobOrder: changedItem.jobOrder,
                            purchaseOrderNumber: changedItem.purchaseOrderNumber,
                            onOffhireDate: changedItem.onOffhireDate,
                            accountChecked: changedItem.accountChecked,
                            goAhead: changedItem.goAhead,
                            rowVersionTimestamp: changedItem.rowVersionTimestamp
                        };
                        saveJobs.push(saveJob);
                    }
                } 
            }

            if (saveJobs.length > 0) {
                var schedule = {
                    scheduleDate: scheduleDate,
                    items: saveJobs,
                    subId: user.sub
                };
                axios.post('api/Schedule/SaveScheduleItems', schedule, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(res => {
                    if (res.data.isError) {
                        var errors = res.data.messages as any[];
                        var scheduleErrors: string[] = [];
                        for (var i = 0; i < errors.length; i++) {
                            toast.error(errors[i].content);
                            scheduleErrors.push(errors[i].content);
                        };
                        scheduleDay.scheduleErrors = scheduleErrors;
                        this.setState({ scheduleDay: scheduleDays });
                    } else {
                        this.updateDayData(scheduleDate, false);
                        toast.success("Schedule Saved");
                    }
                })
                .catch(error => {
                    toast.error(error.message);
                });
            } else {
                toast.info("No items have changed");
            }
        } else {
            toast.error("Cannot find day to update");
        }
    }

    //END ADD NEW / UPDATE

    //ALLOCATE
    goAllocate = (scheduledate: string) => {
        this.props.history.push("/allocate/" + scheduledate);
    }
    //END ALLOCATE

    handleChange = (e: any) => {
        let change: any = {};
        change[e.target.name] = e.target.value;
        this.setState(change);
    }

    handleDateChange = (e: any) => {
        let change: any = {};
        change[e.target.name] = e.target.value;

        //reset scheduled date if set
        change["scheduleDate"] = "";

        this.setState(change);
    }

    handleBranchChange = (e: any) => {
        this.setState({ branchFilterId: e.target.value });
    }

    handleBudgetChange = (e: any) => {
        var budget = e.target.value * 1;
        this.saveMonthlyBudget(budget);
    }

    async saveMonthlyBudget(budget: number) {
        const token = await authService.getAccessToken();

        var monthlyBudget = {
            Month: this.state.month,
            Year: this.state.year,
            Budget: budget
        };

        axios.post('api/Schedule/SaveMonthlyBudget', monthlyBudget, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                for (var i = 0; i < errors.length; i++) {
                    toast.error(errors[i].content);
                };
            } else {
                this.setState({ monthlyBudget: budget, monthlyBudgetFormatted: res.data.budgetFormatted });
                toast.success("Monthly Budget Saved");
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();

        return <div>
            {contents}
        </div>;
    }

    renderDetails = () => {
        let scheduleBranchOptions = this.state.scheduleBranchOptions;
        let scheduleDays = this.state.scheduleDay;

        let scheduleSearch = <ScheduleNewSearchData statusOptions={this.state.statusOptions} showModal={this.state.showSearch} hideModal={this.hideSearchModal} view={this.viewSearchDay} updateScheduleDay={this.updateSearchScheduleDays} />

        return (
            <div>
                <div>
                    {scheduleSearch}
                </div>
                <div id="header" className="schedulenew__search--fixed">
                    <div className="schedulenew__search--fixed-width">
                        <div className="schedulenew__statusBar">
                            <div>
                                <label className="input-group--inline" htmlFor="month">
                                    <select className="select" id="month" name="month" value={this.state.month} onChange={(e) => this.handleDateChange(e)}>
                                        <option defaultValue="-1"></option>
                                        {this.state.monthOptions.map(month =>
                                            <option key={month.id} value={month.id}>{month.name}</option>
                                        )};
                                    </select>
                                </label>
                                <label className="input-group--inline" htmlFor="year">
                                    <select className="select" id="year" name="year" value={this.state.year} onChange={(e) => this.handleDateChange(e)}>
                                        <option defaultValue="-1"></option>
                                        {this.state.yearOptions.map(year =>
                                            <option key={year.id} value={year.id}>{year.name}</option>
                                        )};
                                    </select>
                                </label>
                                <label className="input-group--inline" htmlFor="scheduleDate">
                                    <span className="label">Schedule Date</span>
                                    <input type='date' id="scheduleDate" name="scheduleDate" className='input' value={this.state.scheduleDate} onChange={(e) => this.handleChange(e)}></input>
                                </label>
                                <label className="input-group--inline" htmlFor="branchFilterId">
                                    <span className="label">Branch</span>
                                    <select className="select" id="branchFilterId" name="branchFilterId" value={this.state.branchFilterId} onChange={(e) => this.handleBranchChange(e)}>
                                        {scheduleBranchOptions.map(branch =>
                                            <option key={branch.id} value={branch.id}>{branch.name}</option>
                                        )};
                                    </select>
                                </label>
                                <button className="defaultbutton defaultbutton__medium margin10" type="button" onClick={this.refresh}>Refresh</button>
                                <button className="defaultbutton defaultbutton__small margin10" type="button" onClick={this.resetSearch}>Reset</button>
                            </div>
                            <div className="alignitemscentre">
                                <span>Budget {this.state.allowUpdateMonthlyBudget ? "" : this.state.monthlyBudgetFormatted}</span>
                                <input className={this.state.allowUpdateMonthlyBudget ? "input input--small marginLeft10" : "hidden"} type='number' id="monthlyBudget" name="monthlyBudget" value={this.state.monthlyBudget} onChange={(e) => this.handleBudgetChange(e)} ></input>
                                <span className="marginLeft10">Total {this.state.monthAmountFormatted}</span>
                            </div>
                            <div>
                                <button className="defaultbutton margin10" type="button" onClick={this.search}>Search</button>
                            </div>
                        </div>
                        <div className="marginLeft10">
                            <span className="allocate__customerNoteText">* Items in dark red need Customer unlocked.</span>
                        </div>
                        <div className="marginLeft10">
                            <span className="allocate__customerReckonNoteText">* Items in blue need Customer linked to Reckon.</span>
                        </div>
                        <hr></hr>
                    </div>
                </div>
                <div>
                    <div className="scheduleNewTable schedulenew__day--margin">
                        <div className={this.state.isLoading ? "" : "hidden"}>
                            <p><em>Loading...</em></p>
                        </div>
                        <div className={this.state.isLoading ? "hidden" : "scheduleNewTable table__margin overflowAuto"}>
                            
                            {scheduleDays.map((day, index) =>
                                <div key={index} className="scheduleRow paddingTop10">
                                    <div id={day.isCurrentDay ? "currentday" : ""}></div>
                                    <ScheduleDayData key={index} allowEdit={this.state.allowEdit} allowAllocateToPastDate={this.state.allowAllocateToPastDate} allowUpdateDayLimits={this.state.allowUpdateDayLimits} allowLock={this.state.allowLock} allowGetAccountBalance={this.state.allowGetAccountBalance} loadingDay={this.state.loadingDay} scheduleDay={day} scheduleBranchId={this.state.branchFilterId} noteTypes={this.state.noteTypes} publicHolidays={this.state.publicHolidays} shutdownPeriods={this.state.shutdownPeriods} reasonOptions={this.state.reasonOptions} scheduleTypes={this.state.scheduleTypes} scheduleWarningNo={this.state.scheduleWarningNo} scheduleBookedNo={this.state.scheduleBookedNo} updateScheduleDay={this.updateScheduleDay} updateDay={this.updateDayData} saveDay={this.saveDayData} saveDayOrder={this.saveDayOrder} goAllocate={this.goAllocate} view={this.viewSearchDay } />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}